'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { NewTwitterIcon } from '@/components/ui/icon/brands';
import { Award01Icon, Calendar03Icon, Camera01Icon, ChartBarLineIcon, ChartUpIcon, CheckmarkBadge01Icon, CheckmarkCircle02Icon, Coins01Icon, Edit02Icon, GiftIcon, InformationCircleIconSolid, Layers01Icon, RepeatIcon, StarAward01IconSolid, UserMultiple02Icon } from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';
import { TRADE_HREF } from './trade-link';

/** One bullet in a tip: a contextual icon, a bold title, and a (rich,
 *  `**bold**`-aware) description. */
export interface TipItem {
  icon: IconSvgElement;
  title: string;
  description: string;
}
export interface TipData {
  title: string;
  body: string;
  items: TipItem[];
  foot?: string | undefined;
  /** Replaces the default "Got it" dismiss with a CTA that opens `href` in a new
   *  tab, leaving the current page (and this modal's context) in place. */
  cta?: {
    label: string;
    href: string;
  } | undefined;
}

/** The fixed set of rewards info-tooltips. A precise (not `Record<string,…>`)
 *  shape so callers index a known key without a `| undefined` widening. */
export interface RewardsTips {
  refer: TipData;
  post: TipData;
  streak: TipData;
  clip: TipData;
  quests: TipData;
  board: TipData;
}

/**
 * Split a string on `**bold**` spans, wrapping the emphasised segments in a
 * `<b>` with the given class. Mirrors the mockup's inline `<b>` value emphasis.
 */
function renderRich(text: string, boldClassName: string): React.ReactNode {
  return text.split('**').map((seg, i) => i % 2 === 1 ? <b key={`${String(i)}-${seg}`} className={boldClassName}>
        {seg}
      </b> : seg);
}

/**
 * Pressable info affordance: a small `ⓘ` button that opens the rewards tip in a
 * how-it-works {@link InfoModal} — the exact same modal design used on the
 * portfolio page (title + description header, an icon-left / text-right
 * {@link InfoModal.IconList}, an optional {@link InfoModal.Callout} foot, and a
 * "Got it" footer). Replaces the previous hover/popover tooltip so the content
 * reads identically across the app and is fully legible on touch.
 */
export function InfoTipModal(t0) {
  const $ = _c(25);
  const {
    tip
  } = t0;
  const t = useTranslations("rewards");
  const [open, setOpen] = useState(false);
  const cta = tip.cta;
  let t1;
  if ($[0] !== t) {
    t1 = t("moreInfo");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => setOpen(true);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={InformationCircleIconSolid} className="size-3" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t1) {
    t4 = <button type="button" aria-label={t1} onClick={t2} className="inline-flex shrink-0 cursor-pointer text-foreground-tertiary transition-colors hover:text-foreground-secondary">{t3}</button>;
    $[4] = t1;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = tip.title;
  const t6 = tip.body;
  let t7;
  if ($[6] !== cta || $[7] !== t) {
    t7 = cta !== undefined ? <InfoModal.Footer cta={cta.label} onClose={() => {
      window.open(cta.href, "_blank", "noopener,noreferrer");
      setOpen(false);
    }} /> : <InfoModal.Footer cta={t("gotIt")} onClose={() => setOpen(false)} />;
    $[6] = cta;
    $[7] = t;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  let t8;
  if ($[9] !== tip.items) {
    t8 = tip.items.map(_temp);
    $[9] = tip.items;
    $[10] = t8;
  } else {
    t8 = $[10];
  }
  let t9;
  if ($[11] !== t8) {
    t9 = <InfoModal.IconList>{t8}</InfoModal.IconList>;
    $[11] = t8;
    $[12] = t9;
  } else {
    t9 = $[12];
  }
  let t10;
  if ($[13] !== tip.foot) {
    t10 = tip.foot !== undefined ? <InfoModal.Callout>{renderRich(tip.foot, "font-medium text-foreground-primary")}</InfoModal.Callout> : null;
    $[13] = tip.foot;
    $[14] = t10;
  } else {
    t10 = $[14];
  }
  let t11;
  if ($[15] !== open || $[16] !== t10 || $[17] !== t7 || $[18] !== t9 || $[19] !== tip.body || $[20] !== tip.title) {
    t11 = <InfoModal open={open} onOpenChange={setOpen} size="lg" title={t5} description={t6} footer={t7}>{t9}{t10}</InfoModal>;
    $[15] = open;
    $[16] = t10;
    $[17] = t7;
    $[18] = t9;
    $[19] = tip.body;
    $[20] = tip.title;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11 || $[23] !== t4) {
    t12 = <>{t4}{t11}</>;
    $[22] = t11;
    $[23] = t4;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  return t12;
}

/**
 * Localised tip copy for the rewards info tooltips. Each tip mirrors the
 * `.tip` blocks in the rewards mockup; `**…**` marks inline `<b>`
 * emphasis processed by {@link renderRich}. Every key is referenced statically
 * so the i18n unused-key sweep sees them.
 */
function _temp(item) {
  return <InfoModal.IconItem key={item.title} icon={item.icon} title={item.title} description={renderRich(item.description, "font-medium text-foreground-primary")} />;
}
export function useRewardsTips() {
  const $ = _c(236);
  const t = useTranslations("rewards");
  let t0;
  if ($[0] !== t) {
    t0 = t("tips.refer.title");
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let t1;
  if ($[2] !== t) {
    t1 = t("tips.refer.body");
    $[2] = t;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] !== t) {
    t2 = t("tips.refer.item1Title");
    $[4] = t;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] !== t) {
    t3 = t("tips.refer.item1Body");
    $[6] = t;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  let t4;
  if ($[8] !== t2 || $[9] !== t3) {
    t4 = {
      icon: Coins01Icon,
      title: t2,
      description: t3
    };
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t) {
    t5 = t("tips.refer.item2Title");
    $[11] = t;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  let t6;
  if ($[13] !== t) {
    t6 = t("tips.refer.item2Body");
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t5 || $[16] !== t6) {
    t7 = {
      icon: Layers01Icon,
      title: t5,
      description: t6
    };
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== t) {
    t8 = t("tips.refer.item3Title");
    $[18] = t;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== t) {
    t9 = t("tips.refer.item3Body");
    $[20] = t;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== t8 || $[23] !== t9) {
    t10 = {
      icon: CheckmarkCircle02Icon,
      title: t8,
      description: t9
    };
    $[22] = t8;
    $[23] = t9;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t) {
    t11 = t("tips.refer.item4Title");
    $[25] = t;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== t) {
    t12 = t("tips.refer.item4Body");
    $[27] = t;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== t11 || $[30] !== t12) {
    t13 = {
      icon: UserMultiple02Icon,
      title: t11,
      description: t12
    };
    $[29] = t11;
    $[30] = t12;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t10 || $[33] !== t13 || $[34] !== t4 || $[35] !== t7) {
    t14 = [t4, t7, t10, t13];
    $[32] = t10;
    $[33] = t13;
    $[34] = t4;
    $[35] = t7;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] !== t) {
    t15 = t("tips.refer.foot");
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t0 || $[40] !== t1 || $[41] !== t14 || $[42] !== t15) {
    t16 = {
      title: t0,
      body: t1,
      items: t14,
      foot: t15
    };
    $[39] = t0;
    $[40] = t1;
    $[41] = t14;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== t) {
    t17 = t("tips.post.title");
    $[44] = t;
    $[45] = t17;
  } else {
    t17 = $[45];
  }
  let t18;
  if ($[46] !== t) {
    t18 = t("tips.post.body");
    $[46] = t;
    $[47] = t18;
  } else {
    t18 = $[47];
  }
  let t19;
  if ($[48] !== t) {
    t19 = t("tips.post.item1Title");
    $[48] = t;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== t) {
    t20 = t("tips.post.item1Body");
    $[50] = t;
    $[51] = t20;
  } else {
    t20 = $[51];
  }
  let t21;
  if ($[52] !== t19 || $[53] !== t20) {
    t21 = {
      icon: Coins01Icon,
      title: t19,
      description: t20
    };
    $[52] = t19;
    $[53] = t20;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t) {
    t22 = t("tips.post.item2Title");
    $[55] = t;
    $[56] = t22;
  } else {
    t22 = $[56];
  }
  let t23;
  if ($[57] !== t) {
    t23 = t("tips.post.item2Body");
    $[57] = t;
    $[58] = t23;
  } else {
    t23 = $[58];
  }
  let t24;
  if ($[59] !== t22 || $[60] !== t23) {
    t24 = {
      icon: ChartUpIcon,
      title: t22,
      description: t23
    };
    $[59] = t22;
    $[60] = t23;
    $[61] = t24;
  } else {
    t24 = $[61];
  }
  let t25;
  if ($[62] !== t) {
    t25 = t("tips.post.item3Title");
    $[62] = t;
    $[63] = t25;
  } else {
    t25 = $[63];
  }
  let t26;
  if ($[64] !== t) {
    t26 = t("tips.post.item3Body");
    $[64] = t;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== t25 || $[67] !== t26) {
    t27 = {
      icon: NewTwitterIcon,
      title: t25,
      description: t26
    };
    $[66] = t25;
    $[67] = t26;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== t21 || $[70] !== t24 || $[71] !== t27) {
    t28 = [t21, t24, t27];
    $[69] = t21;
    $[70] = t24;
    $[71] = t27;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== t) {
    t29 = t("tips.post.foot");
    $[73] = t;
    $[74] = t29;
  } else {
    t29 = $[74];
  }
  let t30;
  if ($[75] !== t17 || $[76] !== t18 || $[77] !== t28 || $[78] !== t29) {
    t30 = {
      title: t17,
      body: t18,
      items: t28,
      foot: t29
    };
    $[75] = t17;
    $[76] = t18;
    $[77] = t28;
    $[78] = t29;
    $[79] = t30;
  } else {
    t30 = $[79];
  }
  let t31;
  if ($[80] !== t) {
    t31 = t("tips.streak.title");
    $[80] = t;
    $[81] = t31;
  } else {
    t31 = $[81];
  }
  let t32;
  if ($[82] !== t) {
    t32 = t("tips.streak.body");
    $[82] = t;
    $[83] = t32;
  } else {
    t32 = $[83];
  }
  let t33;
  if ($[84] !== t) {
    t33 = t("tips.streak.item1Title");
    $[84] = t;
    $[85] = t33;
  } else {
    t33 = $[85];
  }
  let t34;
  if ($[86] !== t) {
    t34 = t("tips.streak.item1Body");
    $[86] = t;
    $[87] = t34;
  } else {
    t34 = $[87];
  }
  let t35;
  if ($[88] !== t33 || $[89] !== t34) {
    t35 = {
      icon: GiftIcon,
      title: t33,
      description: t34
    };
    $[88] = t33;
    $[89] = t34;
    $[90] = t35;
  } else {
    t35 = $[90];
  }
  let t36;
  if ($[91] !== t) {
    t36 = t("tips.streak.item2Title");
    $[91] = t;
    $[92] = t36;
  } else {
    t36 = $[92];
  }
  let t37;
  if ($[93] !== t) {
    t37 = t("tips.streak.item2Body");
    $[93] = t;
    $[94] = t37;
  } else {
    t37 = $[94];
  }
  let t38;
  if ($[95] !== t36 || $[96] !== t37) {
    t38 = {
      icon: StarAward01IconSolid,
      title: t36,
      description: t37
    };
    $[95] = t36;
    $[96] = t37;
    $[97] = t38;
  } else {
    t38 = $[97];
  }
  let t39;
  if ($[98] !== t) {
    t39 = t("tips.streak.item3Title");
    $[98] = t;
    $[99] = t39;
  } else {
    t39 = $[99];
  }
  let t40;
  if ($[100] !== t) {
    t40 = t("tips.streak.item3Body");
    $[100] = t;
    $[101] = t40;
  } else {
    t40 = $[101];
  }
  let t41;
  if ($[102] !== t39 || $[103] !== t40) {
    t41 = {
      icon: Calendar03Icon,
      title: t39,
      description: t40
    };
    $[102] = t39;
    $[103] = t40;
    $[104] = t41;
  } else {
    t41 = $[104];
  }
  let t42;
  if ($[105] !== t35 || $[106] !== t38 || $[107] !== t41) {
    t42 = [t35, t38, t41];
    $[105] = t35;
    $[106] = t38;
    $[107] = t41;
    $[108] = t42;
  } else {
    t42 = $[108];
  }
  let t43;
  if ($[109] !== t) {
    t43 = t("tips.streak.foot");
    $[109] = t;
    $[110] = t43;
  } else {
    t43 = $[110];
  }
  let t44;
  if ($[111] !== t) {
    t44 = t("streak.startTrading");
    $[111] = t;
    $[112] = t44;
  } else {
    t44 = $[112];
  }
  let t45;
  if ($[113] !== t44) {
    t45 = {
      label: t44,
      href: TRADE_HREF
    };
    $[113] = t44;
    $[114] = t45;
  } else {
    t45 = $[114];
  }
  let t46;
  if ($[115] !== t31 || $[116] !== t32 || $[117] !== t42 || $[118] !== t43 || $[119] !== t45) {
    t46 = {
      title: t31,
      body: t32,
      items: t42,
      foot: t43,
      cta: t45
    };
    $[115] = t31;
    $[116] = t32;
    $[117] = t42;
    $[118] = t43;
    $[119] = t45;
    $[120] = t46;
  } else {
    t46 = $[120];
  }
  let t47;
  if ($[121] !== t) {
    t47 = t("tips.clip.title");
    $[121] = t;
    $[122] = t47;
  } else {
    t47 = $[122];
  }
  let t48;
  if ($[123] !== t) {
    t48 = t("tips.clip.body");
    $[123] = t;
    $[124] = t48;
  } else {
    t48 = $[124];
  }
  let t49;
  if ($[125] !== t) {
    t49 = t("tips.clip.item1Title");
    $[125] = t;
    $[126] = t49;
  } else {
    t49 = $[126];
  }
  let t50;
  if ($[127] !== t) {
    t50 = t("tips.clip.item1Body");
    $[127] = t;
    $[128] = t50;
  } else {
    t50 = $[128];
  }
  let t51;
  if ($[129] !== t49 || $[130] !== t50) {
    t51 = {
      icon: Coins01Icon,
      title: t49,
      description: t50
    };
    $[129] = t49;
    $[130] = t50;
    $[131] = t51;
  } else {
    t51 = $[131];
  }
  let t52;
  if ($[132] !== t) {
    t52 = t("tips.clip.item2Title");
    $[132] = t;
    $[133] = t52;
  } else {
    t52 = $[133];
  }
  let t53;
  if ($[134] !== t) {
    t53 = t("tips.clip.item2Body");
    $[134] = t;
    $[135] = t53;
  } else {
    t53 = $[135];
  }
  let t54;
  if ($[136] !== t52 || $[137] !== t53) {
    t54 = {
      icon: ChartUpIcon,
      title: t52,
      description: t53
    };
    $[136] = t52;
    $[137] = t53;
    $[138] = t54;
  } else {
    t54 = $[138];
  }
  let t55;
  if ($[139] !== t) {
    t55 = t("tips.clip.item3Title");
    $[139] = t;
    $[140] = t55;
  } else {
    t55 = $[140];
  }
  let t56;
  if ($[141] !== t) {
    t56 = t("tips.clip.item3Body");
    $[141] = t;
    $[142] = t56;
  } else {
    t56 = $[142];
  }
  let t57;
  if ($[143] !== t55 || $[144] !== t56) {
    t57 = {
      icon: Camera01Icon,
      title: t55,
      description: t56
    };
    $[143] = t55;
    $[144] = t56;
    $[145] = t57;
  } else {
    t57 = $[145];
  }
  let t58;
  if ($[146] !== t51 || $[147] !== t54 || $[148] !== t57) {
    t58 = [t51, t54, t57];
    $[146] = t51;
    $[147] = t54;
    $[148] = t57;
    $[149] = t58;
  } else {
    t58 = $[149];
  }
  let t59;
  if ($[150] !== t) {
    t59 = t("tips.clip.foot");
    $[150] = t;
    $[151] = t59;
  } else {
    t59 = $[151];
  }
  let t60;
  if ($[152] !== t47 || $[153] !== t48 || $[154] !== t58 || $[155] !== t59) {
    t60 = {
      title: t47,
      body: t48,
      items: t58,
      foot: t59
    };
    $[152] = t47;
    $[153] = t48;
    $[154] = t58;
    $[155] = t59;
    $[156] = t60;
  } else {
    t60 = $[156];
  }
  let t61;
  if ($[157] !== t) {
    t61 = t("tips.quests.title");
    $[157] = t;
    $[158] = t61;
  } else {
    t61 = $[158];
  }
  let t62;
  if ($[159] !== t) {
    t62 = t("tips.quests.body");
    $[159] = t;
    $[160] = t62;
  } else {
    t62 = $[160];
  }
  let t63;
  if ($[161] !== t) {
    t63 = t("tips.quests.item1Title");
    $[161] = t;
    $[162] = t63;
  } else {
    t63 = $[162];
  }
  let t64;
  if ($[163] !== t) {
    t64 = t("tips.quests.item1Body");
    $[163] = t;
    $[164] = t64;
  } else {
    t64 = $[164];
  }
  let t65;
  if ($[165] !== t63 || $[166] !== t64) {
    t65 = {
      icon: CheckmarkBadge01Icon,
      title: t63,
      description: t64
    };
    $[165] = t63;
    $[166] = t64;
    $[167] = t65;
  } else {
    t65 = $[167];
  }
  let t66;
  if ($[168] !== t) {
    t66 = t("tips.quests.item2Title");
    $[168] = t;
    $[169] = t66;
  } else {
    t66 = $[169];
  }
  let t67;
  if ($[170] !== t) {
    t67 = t("tips.quests.item2Body");
    $[170] = t;
    $[171] = t67;
  } else {
    t67 = $[171];
  }
  let t68;
  if ($[172] !== t66 || $[173] !== t67) {
    t68 = {
      icon: Edit02Icon,
      title: t66,
      description: t67
    };
    $[172] = t66;
    $[173] = t67;
    $[174] = t68;
  } else {
    t68 = $[174];
  }
  let t69;
  if ($[175] !== t) {
    t69 = t("tips.quests.item3Title");
    $[175] = t;
    $[176] = t69;
  } else {
    t69 = $[176];
  }
  let t70;
  if ($[177] !== t) {
    t70 = t("tips.quests.item3Body");
    $[177] = t;
    $[178] = t70;
  } else {
    t70 = $[178];
  }
  let t71;
  if ($[179] !== t69 || $[180] !== t70) {
    t71 = {
      icon: RepeatIcon,
      title: t69,
      description: t70
    };
    $[179] = t69;
    $[180] = t70;
    $[181] = t71;
  } else {
    t71 = $[181];
  }
  let t72;
  if ($[182] !== t65 || $[183] !== t68 || $[184] !== t71) {
    t72 = [t65, t68, t71];
    $[182] = t65;
    $[183] = t68;
    $[184] = t71;
    $[185] = t72;
  } else {
    t72 = $[185];
  }
  let t73;
  if ($[186] !== t) {
    t73 = t("tips.quests.foot");
    $[186] = t;
    $[187] = t73;
  } else {
    t73 = $[187];
  }
  let t74;
  if ($[188] !== t61 || $[189] !== t62 || $[190] !== t72 || $[191] !== t73) {
    t74 = {
      title: t61,
      body: t62,
      items: t72,
      foot: t73
    };
    $[188] = t61;
    $[189] = t62;
    $[190] = t72;
    $[191] = t73;
    $[192] = t74;
  } else {
    t74 = $[192];
  }
  let t75;
  if ($[193] !== t) {
    t75 = t("tips.board.title");
    $[193] = t;
    $[194] = t75;
  } else {
    t75 = $[194];
  }
  let t76;
  if ($[195] !== t) {
    t76 = t("tips.board.body");
    $[195] = t;
    $[196] = t76;
  } else {
    t76 = $[196];
  }
  let t77;
  if ($[197] !== t) {
    t77 = t("tips.board.item1Title");
    $[197] = t;
    $[198] = t77;
  } else {
    t77 = $[198];
  }
  let t78;
  if ($[199] !== t) {
    t78 = t("tips.board.item1Body");
    $[199] = t;
    $[200] = t78;
  } else {
    t78 = $[200];
  }
  let t79;
  if ($[201] !== t77 || $[202] !== t78) {
    t79 = {
      icon: ChartBarLineIcon,
      title: t77,
      description: t78
    };
    $[201] = t77;
    $[202] = t78;
    $[203] = t79;
  } else {
    t79 = $[203];
  }
  let t80;
  if ($[204] !== t) {
    t80 = t("tips.board.item2Title");
    $[204] = t;
    $[205] = t80;
  } else {
    t80 = $[205];
  }
  let t81;
  if ($[206] !== t) {
    t81 = t("tips.board.item2Body", {
      amount: "$10K"
    });
    $[206] = t;
    $[207] = t81;
  } else {
    t81 = $[207];
  }
  let t82;
  if ($[208] !== t80 || $[209] !== t81) {
    t82 = {
      icon: Award01Icon,
      title: t80,
      description: t81
    };
    $[208] = t80;
    $[209] = t81;
    $[210] = t82;
  } else {
    t82 = $[210];
  }
  let t83;
  if ($[211] !== t) {
    t83 = t("tips.board.item3Title");
    $[211] = t;
    $[212] = t83;
  } else {
    t83 = $[212];
  }
  let t84;
  if ($[213] !== t) {
    t84 = t("tips.board.item3Body");
    $[213] = t;
    $[214] = t84;
  } else {
    t84 = $[214];
  }
  let t85;
  if ($[215] !== t83 || $[216] !== t84) {
    t85 = {
      icon: StarAward01IconSolid,
      title: t83,
      description: t84
    };
    $[215] = t83;
    $[216] = t84;
    $[217] = t85;
  } else {
    t85 = $[217];
  }
  let t86;
  if ($[218] !== t79 || $[219] !== t82 || $[220] !== t85) {
    t86 = [t79, t82, t85];
    $[218] = t79;
    $[219] = t82;
    $[220] = t85;
    $[221] = t86;
  } else {
    t86 = $[221];
  }
  let t87;
  if ($[222] !== t) {
    t87 = t("tips.board.foot");
    $[222] = t;
    $[223] = t87;
  } else {
    t87 = $[223];
  }
  let t88;
  if ($[224] !== t75 || $[225] !== t76 || $[226] !== t86 || $[227] !== t87) {
    t88 = {
      title: t75,
      body: t76,
      items: t86,
      foot: t87
    };
    $[224] = t75;
    $[225] = t76;
    $[226] = t86;
    $[227] = t87;
    $[228] = t88;
  } else {
    t88 = $[228];
  }
  let t89;
  if ($[229] !== t16 || $[230] !== t30 || $[231] !== t46 || $[232] !== t60 || $[233] !== t74 || $[234] !== t88) {
    t89 = {
      refer: t16,
      post: t30,
      streak: t46,
      clip: t60,
      quests: t74,
      board: t88
    };
    $[229] = t16;
    $[230] = t30;
    $[231] = t46;
    $[232] = t60;
    $[233] = t74;
    $[234] = t88;
    $[235] = t89;
  } else {
    t89 = $[235];
  }
  return t89 satisfies RewardsTips;
}